import React, { useRef } from 'react';
import { usePopover, DismissButton, Overlay } from '@react-aria/overlays';
import { FocusScope } from '@react-aria/focus';
import type { OverlayTriggerState } from 'react-stately';
import { useBreakpoint } from '../../utils/breakpoint/hooks/useBreakpoint';
import { resolveResponsiveProp } from '../../utils/breakpoint/responsive';
import type { ResponsiveValue } from '../../utils/breakpoint/responsiveSSR';
import styles from './Popover.module.css';

// Define all possible placement options for internal use (menu component)
type PlacementType =
  | 'top'
  | 'top start'
  | 'top end'
  | 'right'
  | 'right top'
  | 'right bottom'
  | 'bottom'
  | 'bottom start'
  | 'bottom end'
  | 'left'
  | 'left top'
  | 'left bottom';

// Define the arrow direction options for the public use
type ArrowDirectionType =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'right'
  | 'bottom'
  | 'left';

// Mapping arrow direction to placement
const arrowDirectionToPlacement: Record<ArrowDirectionType, PlacementType> = {
  top: 'top',
  'top-left': 'top start',
  'top-right': 'top end',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
};

const getPlacementSide = (value?: string | null) => value?.split(' ')[0];

// Internal props that should only be used by specific components like Menu
export interface InternalPopoverProps {
  /**
   * @private Internal use only - placement of the popover
   * This prop should only be used by internal components like Menu
   */
  placement?: PlacementType;

  /**
   * @private Internal use only - whether to allow tabbing out of the popover
   * This prop should only be used by internal components like Menu
   */
  allowTabOut?: boolean;

  /**
   * @private Internal use only - whether to auto-focus elements within the popover
   * This prop should only be used by internal components like Menu
   */
  autoFocus?: boolean;
}

// Public props exposed to all component users
export interface PopoverProps {
  /**
   * Content to be rendered inside the popover
   */
  children: React.ReactNode;

  /**
   * State object controlling the overlay's open/closed status
   */
  state: OverlayTriggerState;

  /**
   *  **[Responsive]** Direction of the arrow that points to the trigger element
   */
  arrowDirection?: ResponsiveValue<ArrowDirectionType>;

  /**
   * Reference to the trigger element that opens the popover
   */
  triggerRef:
    | React.RefObject<HTMLButtonElement | null>
    | { current: HTMLElement | null };

  /**
   * Whether to show the directional arrow on the popover
   */
  hasArrow?: boolean;

  /**
   * Test id for the component
   */
  'data-testid'?: string;
  /** Flips the popover to the opposite side when there is insufficient space. */
  shouldFlip?: boolean;

  /** Whether elements outside the popover can be interacted with. */
  isNonModal?: boolean;
}

// Combined props type for internal use
export type CombinedPopoverProps = PopoverProps & InternalPopoverProps;

export const Popover = ({
  children,
  state,
  arrowDirection = 'bottom',
  triggerRef,
  hasArrow = true,
  placement,
  shouldFlip = true,
  isNonModal = false,
  allowTabOut = false,
  autoFocus = true,
  'data-testid': dataTestId,
}: CombinedPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const currentBreakpoint = useBreakpoint();

  const resolvedArrowDirection = React.useMemo(
    () => resolveResponsiveProp(arrowDirection, currentBreakpoint) ?? 'bottom',
    [arrowDirection, currentBreakpoint],
  );

  // Determine the placement - either from direct placement (internal) or mapped from arrowDirection
  const initialPlacement =
    placement || arrowDirectionToPlacement[resolvedArrowDirection];

  const {
    popoverProps,
    arrowProps,
    placement: finalPlacement,
  } = usePopover(
    {
      popoverRef,
      triggerRef,
      offset: 12,
      placement: initialPlacement,
      shouldFlip,
      isNonModal,
    },
    state,
  );

  const arrowPlacement =
    getPlacementSide(finalPlacement) === getPlacementSide(initialPlacement)
      ? initialPlacement
      : finalPlacement || initialPlacement;

  const normalizedArrowPlacement =
    arrowPlacement === 'top start' || arrowPlacement === 'top end'
      ? arrowPlacement
      : getPlacementSide(arrowPlacement) || arrowPlacement;

  const resolvedArrowStyle = React.useMemo(() => {
    const inlineArrowStyle = arrowProps.style;

    if (!inlineArrowStyle) {
      return undefined;
    }

    // Keep react-aria computed styles generally, but preserve CSS-driven
    // arrow offsets for top start/end variants.
    if (
      normalizedArrowPlacement === 'top start' ||
      normalizedArrowPlacement === 'top end'
    ) {
      const nonPositionalStyles = Object.fromEntries(
        Object.entries(inlineArrowStyle).filter(
          ([key]) =>
            key !== 'left' &&
            key !== 'right' &&
            key !== 'top' &&
            key !== 'bottom',
        ),
      );

      return nonPositionalStyles;
    }

    return inlineArrowStyle;
  }, [arrowProps.style, normalizedArrowPlacement]);

  return (
    <Overlay>
      <FocusScope restoreFocus contain={!allowTabOut} autoFocus={autoFocus}>
        <div
          {...popoverProps}
          ref={popoverRef}
          className={styles.popover}
          data-testid={dataTestId}
        >
          <DismissButton onDismiss={state.close} />
          {children}
          {hasArrow && (
            <div
              {...arrowProps}
              className={styles.arrow}
              style={resolvedArrowStyle}
              data-placement={normalizedArrowPlacement}
            />
          )}
        </div>
      </FocusScope>
    </Overlay>
  );
};

export default Popover;
